import React from 'react';

/**
 * 🧠 XAVFSIZLIK BILIMLARI — admin paneli.
 *
 * Bu yerda «admin hal qiladigan» narsalar turadi: hujumlar katalogi, himoya
 * usullari, tekshiruv taʼriflari, AI oʻrganish takliflari, tashqi manbalar
 * (NVD/OWASP/MITRE/GHSA), skan saboqlari va dvigatel loglari. Ilgari bularning
 * hammasi ALOHIDA Security ilovasida edi — endi Khorezmai admini ichida.
 *
 * Nega Studio emas, ADMIN: bu maʼlumotnoma butun platformaga tegishli (bitta
 * loyihaniki emas) va uni oʻzgartirish HAR skanning natijasiga taʼsir qiladi.
 *
 * `authFetch` har render'da yangi identity — effekt dep'iga QOʻYILMAYDI (refetch-storm).
 */

const TABS = [
  { k: 'proposals',   t: '💡 Takliflar',    d: 'AI va tashqi manbalar taklif qilgan yangi bilim — tasdiqlasangiz bazaga tushadi' },
  { k: 'attacks',     t: '🎯 Hujumlar',     d: 'Hujum turlari katalogi — topilmalar shu bilan bogʻlanadi' },
  { k: 'protections', t: '🛡 Himoyalar',    d: 'Har hujumga qarshi amaliy himoya usullari' },
  { k: 'checks',      t: '✅ Tekshiruvlar', d: 'Server auditida bajariladigan taʼriflar — oʻchirilgani skanda ishlamaydi' },
  { k: 'feeds',       t: '📡 Manbalar',     d: 'Tashqi bilim oqimlari: NVD, GitHub Advisory, MITRE ATT&CK, OWASP' },
  { k: 'insights',    t: '🧠 Saboqlar',     d: 'Dvigatel skanlardan oʻrgangan xulosalar' },
  { k: 'logs',        t: '📜 Loglar',       d: 'Dvigatel audit jurnali' },
];

const SEV = {
  CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400',
  MEDIUM: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', LOW: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  INFO: 'bg-gray-100 dark:bg-gray-700 text-gray-400',
};
const IMP = { HIGH: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', MEDIUM: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400', LOW: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' };
const SEV_OPTS = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function SecurityKbPanel({ authFetch, apiUrl }) {
  const [tab, setTab] = React.useState('proposals');
  const [summary, setSummary] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [sev, setSev] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [sel, setSel] = React.useState([]);        // tanlangan taklif ID'lari
  const [open, setOpen] = React.useState(null);    // ochilgan element
  const [toast, setToast] = React.useState('');
  const [jobs, setJobs] = React.useState(null);    // uzun amallar holati
  const afRef = React.useRef(authFetch);
  afRef.current = authFetch;
  const LIMIT = 25;

  const say = (m) => { setToast(m); setTimeout(() => setToast(''), 4000); };

  const call = React.useCallback(async (path, opts) => {
    const r = await afRef.current(`${apiUrl}/security/kb${path}`, opts);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j;
  }, [apiUrl]);

  const loadSummary = React.useCallback(async () => {
    try { setSummary((await call('/summary')).summary); } catch { /* sarlavha — jim */ }
  }, [call]);

  const load = React.useCallback(async (t, pg, q, sv) => {
    setLoading(true); setErr(''); setOpen(null);
    try {
      let path;
      if (t === 'feeds') path = '/feeds';
      else {
        const p = new URLSearchParams({ limit: String(LIMIT), page: String(pg) });
        if (q) p.set('search', q);
        if (sv && (t === 'attacks')) p.set('severity', sv);
        if (t === 'proposals') p.set('status', 'PROPOSED');
        path = `/${t}?${p}`;
      }
      const j = await call(path);
      setItems(j.items || []); setTotal(j.total ?? (j.items || []).length);
    } catch (e) { setErr(e.message); setItems([]); }
    finally { setLoading(false); }
  }, [call]);

  React.useEffect(() => { loadSummary(); }, [loadSummary]);
  React.useEffect(() => { load(tab, page, search, sev); }, [tab, page, sev, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uzun amallar (baholash / manbalarni yangilash) fonda ketadi — holatini kuzatamiz.
  React.useEffect(() => {
    if (!jobs) return;
    const running = Object.values(jobs).some(j => j && j.running);
    if (!running) return;
    const id = setTimeout(async () => {
      try {
        const j = await call('/jobs');
        setJobs(j.jobs);
        const stillRunning = Object.values(j.jobs || {}).some(x => x && x.running);
        if (!stillRunning) { say('✅ Amal tugadi'); loadSummary(); load(tab, page, search, sev); }
      } catch { /* jim */ }
    }, 5000);
    return () => clearTimeout(id);
  }, [jobs, call, tab, page, search, sev, load, loadSummary]);

  function switchTab(k) { setTab(k); setPage(1); setSearch(''); setSev(''); setSel([]); }
  const toggleSel = (id) => setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  async function act(path, opts, okMsg, key) {
    setBusy(key || path);
    try { await call(path, opts); say(okMsg); await load(tab, page, search, sev); await loadSummary(); }
    catch (e) { say('❌ ' + e.message); }
    finally { setBusy(''); }
  }

  const jsonPost = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

  async function bulk(action) {
    if (!sel.length) return;
    if (action === 'approve' && !window.confirm(`${sel.length} ta taklif bazaga QOʻSHILADI. Davom etamizmi?`)) return;
    setBusy('bulk');
    try {
      const j = await call(`/proposals/bulk/${action}`, jsonPost({ ids: sel }));
      say(`✅ ${action === 'approve' ? 'Tasdiqlandi' : 'Rad etildi'}: ${JSON.stringify(j.result || {}).slice(0, 80)}`);
      setSel([]); await load(tab, page, search, sev); await loadSummary();
    } catch (e) { say('❌ ' + e.message); }
    finally { setBusy(''); }
  }

  async function startJob(path, msg) {
    setBusy(path);
    try { const j = await call(path, jsonPost({})); setJobs(j.job ? { ...(jobs || {}), tmp: j.job } : null); say(msg); }
    catch (e) { say('❌ ' + e.message); }
    finally { setBusy(''); }
    try { setJobs((await call('/jobs')).jobs); } catch { /* jim */ }
  }

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const tabInfo = TABS.find(x => x.k === tab) || {};
  const runningJob = jobs && Object.entries(jobs).find(([, j]) => j && j.running);

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm shadow-lg">{toast}</div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">🧠 Xavfsizlik bilimlari</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Skaner shu bilimlar asosida ishlaydi: qaysi hujumlarni biladi, qaysi tekshiruvni bajaradi, nimani tavsiya qiladi.
        </p>
      </div>

      {summary && (
        <div className="flex flex-wrap gap-2">
          {[
            ['🎯', 'hujum', summary.attacks, 'attacks'],
            ['🛡', 'himoya', summary.protections, 'protections'],
            ['✅', 'tekshiruv', summary.checks, 'checks'],
            ['💡', 'kutayotgan taklif', summary.proposals, 'proposals'],
            ['🧠', 'saboq', summary.insights, 'insights'],
            ['📡', 'manba', `${summary.feedsEnabled}/${summary.feeds}`, 'feeds'],
          ].map(([ic, lbl, n, k]) => (
            <button key={lbl} onClick={() => switchTab(k)}
              className={`px-3 py-2 rounded-lg border text-left transition ${tab === k ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
              <div className="text-base font-semibold text-gray-900 dark:text-white">{ic} {n}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{lbl}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 border-b border-gray-200 dark:border-gray-700 pb-2">
        {TABS.map(x => (
          <button key={x.k} onClick={() => switchTab(x.k)} title={x.d}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${tab === x.k ? 'bg-primary-700 dark:bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            {x.t}
          </button>
        ))}
      </div>
      {tabInfo.d && <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">{tabInfo.d}</p>}

      {runningJob && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
          ⏳ Fonda ishlayapti ({runningJob[0]}) — bir necha daqiqa olishi mumkin. Tugagach roʻyxat oʻzi yangilanadi.
        </div>
      )}

      {tab !== 'feeds' && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(tab, 1, search, sev); } }}
            placeholder="Qidirish…" className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm w-56" />
          <button onClick={() => { setPage(1); load(tab, 1, search, sev); }}
            className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">🔎 Qidirish</button>
          {tab === 'attacks' && (
            <select value={sev} onChange={e => { setSev(e.target.value); setPage(1); }}
              className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm bg-white dark:bg-gray-800">
              {SEV_OPTS.map(o => <option key={o} value={o}>{o || 'barcha jiddiylik'}</option>)}
            </select>
          )}
          <span className="text-xs text-gray-400">jami {total}</span>
          <button onClick={() => { load(tab, page, search, sev); loadSummary(); }}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">↻ Yangilash</button>
        </div>
      )}

      {tab === 'proposals' && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 p-2.5">
          <span className="text-xs text-gray-600 dark:text-gray-400">{sel.length ? `${sel.length} ta tanlandi` : 'Tanlab tasdiqlang yoki rad eting'}</span>
          <button onClick={() => bulk('approve')} disabled={!sel.length || busy === 'bulk'}
            className="px-2.5 py-1 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">✅ Tasdiqlash</button>
          <button onClick={() => bulk('reject')} disabled={!sel.length || busy === 'bulk'}
            className="px-2.5 py-1 rounded-lg text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-40">⏭ Rad etish</button>
          <button onClick={() => setSel(items.map(i => i.id))} className="px-2.5 py-1 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">Sahifani belgilash</button>
          <button onClick={() => startJob('/proposals/assess', '🤖 Baholash fonda boshlandi')}
            disabled={busy === '/proposals/assess'}
            title="AI har taklifning muhimligini baholaydi (HIGH/MEDIUM/LOW) — keyin muhimlarini tanlash oson"
            className="ml-auto px-2.5 py-1 rounded-lg text-xs bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-40">
            🤖 Muhimligini baholash
          </button>
        </div>
      )}

      {err && <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">{err}</div>}
      {loading && <div className="text-sm text-gray-400">Yuklanmoqda…</div>}

      {!loading && !err && (
        <div className="space-y-2">
          {!items.length && <div className="text-sm text-gray-400">Hech narsa topilmadi.</div>}

          {items.map(it => {
            const id = it.id || it.key;
            const isOpen = open === id;
            return (
              <div key={id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="p-3 flex items-start gap-2.5 flex-wrap">
                  {tab === 'proposals' && (
                    <input type="checkbox" className="mt-1 w-4 h-4 accent-primary-700"
                      checked={sel.includes(it.id)} onChange={() => toggleSel(it.id)} />
                  )}
                  <div className="flex-1 min-w-[16rem]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {it.severity && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${SEV[it.severity] || 'bg-gray-100 dark:bg-gray-700'}`}>{String(it.severity).toLowerCase()}</span>}
                      {it.importance && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${IMP[it.importance] || 'bg-gray-100 dark:bg-gray-700'}`}>muhimlik: {String(it.importance).toLowerCase()}</span>}
                      {it.type && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{it.type}</span>}
                      {it.source && <span className="px-1.5 py-0.5 rounded text-[10px] bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">{it.source}</span>}
                      {it.category && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{it.category}</span>}
                      {tab === 'checks' && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${it.enabled ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`}>
                          {it.enabled ? 'yoqilgan' : 'oʻchirilgan'}
                        </span>
                      )}
                      {tab === 'feeds' && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${it.enabled ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}`}>
                          {it.enabled ? 'yoqilgan' : 'oʻchirilgan'}
                        </span>
                      )}
                      {it.lastStatus && <span className={`px-1.5 py-0.5 rounded text-[10px] ${it.lastStatus === 'OK' ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'}`}>{it.lastStatus}</span>}
                      {it.level && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{it.level}</span>}
                    </div>
                    <button onClick={() => setOpen(isOpen ? null : id)} className="text-left w-full">
                      <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{it.title || it.name || it.action || it.key}</div>
                      {(it.summary || it.detail || it.reasoning || it.description) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{String(it.summary || it.detail || it.reasoning || it.description).slice(0, 220)}</p>
                      )}
                    </button>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {it.createdAt && fmtDate(it.createdAt)}
                      {it.lastRunAt && ` · oxirgi: ${fmtDate(it.lastRunAt)}`}
                      {typeof it.totalProposed === 'number' && ` · jami taklif: ${it.totalProposed}`}
                      {it.key && tab === 'checks' && ` · ${it.key}`}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                    {tab === 'proposals' && (<>
                      <button onClick={() => act(`/proposals/${it.id}/approve`, jsonPost({}), '✅ Bazaga qoʻshildi', it.id)}
                        disabled={busy === it.id}
                        className="px-2.5 py-1 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">✅</button>
                      <button onClick={() => act(`/proposals/${it.id}/reject`, jsonPost({}), '⏭ Rad etildi', it.id)}
                        disabled={busy === it.id}
                        className="px-2.5 py-1 rounded-lg text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-40">⏭</button>
                    </>)}
                    {tab === 'checks' && (
                      <button onClick={() => act(`/checks/${it.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !it.enabled }) },
                        it.enabled ? '⏸ Oʻchirildi' : '▶ Yoqildi', it.id)}
                        disabled={busy === it.id}
                        title={it.enabled ? 'Bu tekshiruv skanda bajarilmasin' : 'Bu tekshiruv skanda bajarilsin'}
                        className="px-2.5 py-1 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">
                        {it.enabled ? '⏸ Oʻchirish' : '▶ Yoqish'}
                      </button>
                    )}
                    {tab === 'feeds' && (<>
                      <button onClick={() => act(`/feeds/${it.key}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !it.enabled }) },
                        it.enabled ? '⏸ Oʻchirildi' : '▶ Yoqildi', it.key)}
                        disabled={busy === it.key}
                        className="px-2.5 py-1 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">
                        {it.enabled ? '⏸ Oʻchirish' : '▶ Yoqish'}
                      </button>
                      <select value={it.minSeverity || 'HIGH'} disabled={busy === it.key}
                        onChange={e => act(`/feeds/${it.key}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ minSeverity: e.target.value }) }, '✅ Saqlandi', it.key)}
                        title="Shu darajadan pastini taklif qilmasin (flood'ni kamaytiradi)"
                        className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-1.5 py-1 bg-white dark:bg-gray-800">
                        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(o => <option key={o} value={o}>≥ {o}</option>)}
                      </select>
                    </>)}
                    {(tab === 'attacks' || tab === 'protections') && (
                      <button onClick={() => { if (window.confirm(`Oʻchirilsinmi?\n\n${it.name}`)) act(`/${tab}/${it.id}`, { method: 'DELETE' }, '🗑 Oʻchirildi', it.id); }}
                        disabled={busy === it.id}
                        className="px-2.5 py-1 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/60 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-40">🗑</button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="px-3 pb-3 space-y-1.5 border-t border-gray-100 dark:border-gray-700 pt-2">
                    {it.description && <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{it.description}</p>}
                    {it.technique && <div className="text-xs text-gray-600 dark:text-gray-400"><b>Texnika:</b> {it.technique}</div>}
                    {it.indicators && <div className="text-xs text-gray-600 dark:text-gray-400"><b>Belgilari:</b> {it.indicators}</div>}
                    {it.implementation && <div className="text-xs text-gray-600 dark:text-gray-400"><b>Joriy qilish:</b> {it.implementation}</div>}
                    {it.command && <pre className="text-[11px] bg-gray-900 text-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{it.command}</pre>}
                    {it.remediationHint && <div className="text-xs text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 rounded p-2">{it.remediationHint}</div>}
                    {it.importanceReason && <div className="text-xs text-gray-600 dark:text-gray-400"><b>AI bahosi:</b> {it.importanceReason}</div>}
                    {it.reasoning && <div className="text-xs text-gray-600 dark:text-gray-400"><b>Sabab:</b> {it.reasoning}</div>}
                    {Array.isArray(it.appliesWhen) && it.appliesWhen.length > 0 && (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">Shart: {it.appliesWhen.join(', ')}</div>
                    )}
                    {it.payload && (
                      <pre className="text-[10px] bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded p-2 max-h-52 overflow-auto whitespace-pre-wrap break-all">
                        {JSON.stringify(it.payload, null, 2)}
                      </pre>
                    )}
                    {Array.isArray(it.references) && it.references.length > 0 && (
                      <div className="text-[10px] break-all">
                        {it.references.slice(0, 8).map((r, i) => (
                          <a key={i} href={r} target="_blank" rel="noreferrer" className="text-primary-700 dark:text-primary-500 hover:underline mr-2">manba{i + 1}</a>
                        ))}
                      </div>
                    )}
                    {it.lastError && <div className="text-xs text-red-700 dark:text-red-400">{it.lastError}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'feeds' && (
        <button onClick={() => startJob('/feeds/run', '📡 Manbalar yangilanmoqda (fonda)')}
          disabled={busy === '/feeds/run'}
          title="Barcha yoqilgan manbalardan yangi maʼlumot oladi va taklif qilib qoʻyadi"
          className="px-3 py-1.5 rounded-lg text-sm bg-primary-700 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-40">
          📡 Manbalarni hozir yangilash
        </button>
      )}

      {tab !== 'feeds' && pages > 1 && (
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">← Oldingi</button>
          <span className="text-xs text-gray-500 dark:text-gray-400">{page} / {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
            className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">Keyingi →</button>
        </div>
      )}
    </div>
  );
}
